export type Language = 'en' | 'pl';

export interface Translations {
  [key: string]: string | string[] | Translations;
}

export const translations: Record<Language, Translations> = {
  en: {
    common: {
      brand: 'Skycodee',
      home: 'Home',
      explore: 'Explore',
      learnMore: 'Learn more',
      backHome: 'Back to home',
      watchOnYouTube: 'Watch on YouTube',
      getInTouch: 'Get in touch',
      talkToUs: 'Talk to us',
      comingSoon: 'Coming soon',
      inDevelopment: 'In development',
      earlyAccess: 'Early access',
      prototype: 'Prototype',
      whatWeBuild: 'What we build',
      highlights: 'Highlights',
      gallery: 'Gallery',
      status: 'Status',
    },
    nav: {
      ai: 'AI',
      gamedev: 'GameDev',
      avionics: 'Avionics',
      teach: 'Teach&Learn',
      contact: 'Contact',
    },
    hero: {
      eyebrow: 'One studio · four skies',
      titleA: 'We build software that',
      titleB: 'flies, plays and teaches',
      lead: 'Skycodee is an engineering studio built around applied AI. We ship production AI products, first-person games, autonomous drone swarms and language-learning experiences for children — all under one roof.',
      ctaPrimary: 'Explore our divisions',
      ctaSecondary: 'Work with us',
      statDivisions: 'Divisions',
      statFocus: 'Applied AI',
      statFocusSub: 'in everything we ship',
      statHq: 'Poland',
      statHqSub: 'building worldwide',
    },
    tiles: {
      eyebrow: 'Divisions',
      title: 'Four teams, one engineering culture',
      lead: 'Each division runs its own roadmap, but shares the same core: applied AI, tight hardware/software loops and a lot of shipping.',
    },
    ai: {
      name: 'Skycodee.AI',
      tagline: 'The core studio — applied AI products, agents and platforms.',
      pitch: 'Our main division. We design and ship AI-native products end to end: agent platforms, LLM pipelines, retrieval systems and the boring-but-critical infrastructure that keeps them running in production.',
      badge: 'Main division',
      f1: 'AI-native product engineering',
      f2: 'Agent platforms & LLM pipelines',
      f3: 'Full-stack delivery, from idea to production',
      heroTitle: 'AI products, built to survive production',
      heroLead: 'Skycodee.AI is the studio at the centre of everything else on this page. The models, tooling and infrastructure we build here power our games, our drones and our classroom.',
      s1t: 'Agent platforms',
      s1d: 'Multi-step agents with tool use, guardrails and human-in-the-loop review — designed so a non-engineer can actually operate them.',
      s2t: 'LLM pipelines & RAG',
      s2d: 'Retrieval, evaluation and prompt orchestration that stays measurable. We treat prompts as versioned artifacts, not folklore.',
      s3t: 'Product engineering',
      s3d: 'React, .NET, Python, containers, CI. We build the whole thing, not just the demo notebook.',
      s4t: 'Applied R&D',
      s4d: 'Voice, vision and generative media — the same building blocks we reuse across GameDev, Avionics and Teach&Learn.',
    },
    game: {
      name: 'Skycodee.GameDev',
      tagline: 'GhostEagles — local split-screen FPS for up to 4 players + wireless VR on Quest 3.',
      pitch: 'An ambitious project designed to bring players back to the world of local split-screen and couch gaming at a single party. Currently featuring an advanced prototype built in Unity 6 with a full game menu, custom maps, and versatile play modes.',
      badge: 'GhostEagles',
      f1: 'Local split-screen up to 4 players + Quest 3 VR',
      f2: 'Unity 6 engine · 130-140 FPS on RTX 4050',
      f3: 'Gameplay videos on our YouTube channel',
      heroTitle: 'GhostEagles',
      heroLead: 'Gather together and play with up to 4 players on local split-screen in one room + 1 player on Meta Quest 3 VR wirelessly!',
      s1t: 'Local Split-Screen + Wireless VR (Meta Quest 3)',
      s1d: 'Up to 4 players on a single split-screen + 1 player in wireless VR on Meta Quest 3. A true return to couch multiplayer in the same room!',
      s2t: '4 Custom Maps & Poznań Replica (AI Street View Textures)',
      s2d: 'Features 4 custom maps (Desert, Absolute Night, Evening Factory, Rainy, Winter) plus a realistic replica of the Poznań Główna district. Includes an automated building creator using Google Street View textures processed by LLMs.',
      s3t: 'Versatile Game Modes',
      s3d: 'Full gameplay flexibility: Solo mode, Split-screen, Co-op, Players vs Bots, mixed setups, and fast-paced Deathmatch.',
      s4t: 'Unity 6 Engine & High Performance (130-140 FPS)',
      s4d: 'Built in Unity 6 with heavy focus on optimization. Runs at a smooth 130-140 FPS on a laptop RTX 4050 GPU depending on the map.',
      s5t: 'Ambitious Roadmap & Future Plans',
      s5d: 'Finalizing the complete gameplay loop with scoring and win/loss rules (currently a sandbox for feature testing), fine-tuning controller responsiveness (mouse + gamepads), aligning with modern standards, and optimizing for lower-spec machines.',
      youtubeCta: 'Gameplay on YouTube',
      youtubeDesc: 'Playtests, build updates and full split-screen matches.',
      youtubeChannelLink: 'Watch all videos on YouTube channel',
      carouselBadge: 'Gameplay',
      carouselTitle: 'Gameplay Showcase',
    },
    avionics: {
      name: 'Skycodee.Avionics',
      tagline: 'Drone swarms for people — delivery drones and an eye in the sky.',
      pitch: 'A drone-building group working on a swarm for everyday use: small cargo drones that carry a payload about the size of three potatoes with a gripper claw, plus an observer drone coordinating them from above.',
      badge: 'Drone swarm',
      f1: 'Drop drones with a gripper claw',
      f2: 'Payload roughly the size of three potatoes',
      f3: 'Observer drone as the swarm\'s eye',
      heroTitle: 'A drone swarm for people',
      heroLead: 'Rój dronów dla ludzi. Not a defence programme and not a toy — a practical swarm that picks something up, carries it, and puts it down where you need it.',
      s1t: 'Drop drone',
      s1d: 'Carries a payload about the size of three potatoes. A servo gripper grabs, holds and releases the package without landing.',
      s2t: 'Observer drone',
      s2d: 'Loiters above the swarm, keeps the map, tracks each unit and hands out targets. The others fly light because it does the seeing.',
      s3t: 'Swarm coordination',
      s3d: 'Shared position and task state across the fleet, so units divide the job instead of racing each other for it.',
      s4t: 'Built in the open',
      s4d: 'Off-the-shelf frames, custom mounts, printed grippers and our own flight logic — iterated in the field, not on a slide.',
      shot1: 'Drop drone with gripper claw',
      shot2: 'Observer drone — swarm overwatch',
      shot3: 'Swarm formation over the drop zone',
      specPayload: 'Payload',
      specPayloadV: '~3 potatoes',
      specGrip: 'Grip',
      specGripV: 'Servo claw',
      specRole: 'Observer',
      specRoleV: 'Eye in the sky',
    },
    teach: {
      name: 'Skycodee.TeachAndLearn',
      tagline: 'An AI game that teaches children words — in mum\'s voice.',
      pitch: 'A cozy, personalized learning game. Generated scenes place a child inside their favourite environment, ElevenLabs voice brings the story to life, and a parent\'s own voice guides them word by word — in any language.',
      badge: 'AI learning game',
      f1: 'Learn words in any language',
      f2: 'Generated cozy, favourite environments',
      f3: 'Guided by mum\'s own voice',
      heroTitle: 'Learning words, in a voice they trust',
      heroLead: 'Children learn language from the people they love. We built a game that keeps that voice in the loop — and lets AI take care of the rest.',
      s1t: 'Mum\'s voice, on every word',
      s1d: 'A short voice sample is enough. ElevenLabs voice synthesis then narrates every prompt, hint and celebration in a parent\'s own voice.',
      s2t: 'Any language',
      s2d: 'Pick the language the child is learning and the one they already speak. The game bridges the two, word by word.',
      s3t: 'Cozy generated worlds',
      s3d: 'Image generation builds the child\'s favourite environment — a forest den, a space station, grandma\'s kitchen — and teaches the words that live in it.',
      s4t: 'Personalized pacing',
      s4d: 'The game remembers what stuck and what did not, and quietly brings the hard words back until they land.',
      shot1: 'Cozy forest den — animals & colours',
      shot2: 'Word card with mum\'s voice prompt',
      shot3: 'Space station scene — objects & verbs',
      techVoice: 'Voice',
      techVoiceV: 'ElevenLabs',
      techImage: 'Scenes',
      techImageV: 'Image generation',
      techLang: 'Languages',
      techLangV: 'Any pair',
    },
    cta: {
      title: 'Building something that flies, plays or teaches?',
      lead: 'We take on a small number of partner projects each year across all four divisions.',
      button: 'Talk to Skycodee',
    },
    footer: {
      tagline: 'AI · GameDev · Avionics · Teach&Learn',
      divisions: 'Divisions',
      note: 'This is a presentation site. Production domain: skycodee.com',
      rights: 'All rights reserved.',
    },
    contact: {
      name: 'Skycodee.Contact',
      badge: 'HQ & Contact',
      heroTitle: 'Studio HQ & Contact',
      heroLead: 'Get in touch with Skycodee or learn about our autonomous, AI-supervised engineering studio based in Poznań, Poland.',
      aboutTitle: 'About Us',
      aboutText: 'We are a software development company. A human engineer here only supervises machines 24/7 through alert & monitoring tools and a machine communication interface. During this time, software across several industries is being developed.',
      originTitle: 'Our Origins',
      originText: 'The company grew out of many years of programming experience originating from Główna, Poznań.',
      authorTitle: 'Author & Lead Engineer',
      authorTagline: 'Systems engineering, artificial intelligence & software architecture',
      mapTitle: 'Skycode HQ Location',
      mapSubtitle: 'Główna, Poznań, Poland',
      pinLabel: 'Siedziba Skycode',
      directContact: 'Direct Contact',
      emailUs: 'Send email',
      sendMessage: 'Write to us',
    },
  },

  pl: {
    common: {
      brand: 'Skycodee',
      home: 'Start',
      explore: 'Zobacz',
      learnMore: 'Dowiedz się więcej',
      backHome: 'Powrót na stronę główną',
      watchOnYouTube: 'Zobacz na YouTube',
      getInTouch: 'Napisz do nas',
      talkToUs: 'Porozmawiajmy',
      comingSoon: 'Wkrótce',
      inDevelopment: 'W budowie',
      earlyAccess: 'Wczesny dostęp',
      prototype: 'Prototyp',
      whatWeBuild: 'Co budujemy',
      highlights: 'Najważniejsze',
      gallery: 'Galeria',
      status: 'Status',
    },
    nav: {
      ai: 'AI',
      gamedev: 'GameDev',
      avionics: 'Awionika',
      teach: 'Teach&Learn',
      contact: 'Kontakt',
    },
    hero: {
      eyebrow: 'Jedno studio · cztery nieba',
      titleA: 'Tworzymy oprogramowanie, które',
      titleB: 'lata, gra i uczy',
      lead: 'Skycodee to studio inżynierskie zbudowane wokół zastosowanej AI. Dostarczamy produkcyjne produkty AI, gry FPS, autonomiczne roje dronów i aplikacje do nauki języków dla dzieci — wszystko pod jednym dachem.',
      ctaPrimary: 'Poznaj nasze działy',
      ctaSecondary: 'Współpraca',
      statDivisions: 'Działy',
      statFocus: 'Zastosowana AI',
      statFocusSub: 'w każdym projekcie',
      statHq: 'Polska',
      statHqSub: 'projekty globalnie',
    },
    tiles: {
      eyebrow: 'Działy',
      title: 'Cztery zespoły, jedna kultura inżynierska',
      lead: 'Każdy dział ma własną mapę drogową, ale wspólny rdzeń: zastosowaną AI, ścisłe połączenie sprzętu z oprogramowaniem i dużo wdrożeń.',
    },
    ai: {
      name: 'Skycodee.AI',
      tagline: 'Główny dział — produkty AI, agenci i platformy.',
      pitch: 'Nasz główny dział. Projektujemy i wdrażamy produkty oparte na AI od początku do końca: platformy agentowe, potoki LLM, systemy wyszukiwania i całą nudną, ale krytyczną infrastrukturę, która utrzymuje je na produkcji.',
      badge: 'Główny dział',
      f1: 'Produkty inżynieryjne oparte na AI',
      f2: 'Platformy agentowe i potoki LLM',
      f3: 'Pełen zakres — od pomysłu do produkcji',
      heroTitle: 'Produkty AI, które wytrzymują produkcję',
      heroLead: 'Skycodee.AI to studio w centrum wszystkiego, co widzisz na tej stronie. Modele, narzędzia i infrastruktura, które tu budujemy, napędzają nasze gry, drony i naszą klasę.',
      s1t: 'Platformy agentowe',
      s1d: 'Wieloetapowi agenci z dostępem do narzędzi, zabezpieczeniami i weryfikacją człowieka — zaprojektowani tak, by obsłużyła je osoba nietechniczna.',
      s2t: 'Potoki LLM i RAG',
      s2d: 'Wyszukiwanie, ewaluacja i orkiestracja promptów, które da się zmierzyć. Traktujemy prompty jak wersjonowane artefakty, nie jak legendy.',
      s3t: 'Inżynieria produktu',
      s3d: 'React, .NET, Python, kontenery, CI. Budujemy całość, a nie tylko notatnik z demem.',
      s4t: 'Badania i rozwój',
      s4d: 'Głos, wizja i media generatywne — te same klocki wykorzystujemy w GameDev, Awionice i Teach&Learn.',
    },
    game: {
      name: 'Skycodee.GameDev',
      tagline: 'GhostEagles — lokalny splitscreen FPS do 4 graczy + bezprzewodowy VR na Quest 3.',
      pitch: 'Ambitny projekt, który ma zabrać graczy w świat lokalnego splitscreena i czasu wspólnie spędzonego na kanapie na jednej imprezie. Gra oferuje aktualnie dopracowaną wersję prototypową w Unity 6 z rozwiniętym menu gry, zróżnicowanymi mapami oraz zaawansowanymi trybami rozgrywki.',
      badge: 'GhostEagles',
      f1: 'Lokalny splitscreen do 4 graczy + VR Quest 3',
      f2: 'Silnik Unity 6 · 130-140 FPS na RTX 4050',
      f3: 'Gameplaye na naszym kanale YouTube',
      heroTitle: 'GhostEagles',
      heroLead: 'Spotkajcie się i grajcie do 4 graczy w jednym pokoju na splitscreenie + jeden gracz na platformie Meta Quest 3 na VRze bezprzewodowo.',
      s1t: 'Lokalny Splitscreen + VR (Meta Quest 3)',
      s1d: 'Spotkajcie się i grajcie do 4 graczy w jednym pokoju na podzielonym ekranie + jeden gracz na platformie Meta Quest 3 na VRze bezprzewodowo.',
      s2t: '4 Mapy & Poznań Główna (Generowanie AI z Street View)',
      s2d: 'Aktualnie w prototypie z rozwiniętym menu gry: 4 mapy (pustynna, noc absolutna, wieczór fabryka, mapa deszczowa, mapa zimowa) oraz odtworzenie jednej z dzielnic Poznania Głównej. W budowie kreator automatycznego nanoszenia budynków z teksturami automatycznie branymi z Google Street View i obrobionymi LLMem do postaci tekstury.',
      s3t: 'Wiele Trybów Rozgrywki',
      s3d: 'Tryb solo, tryb splitscreen, tryb coop, tryb gracze vs boty, tryby wymieszane lub klasyczny deathmatch.',
      s4t: 'Dewelopowane w Unity 6 (130-140 FPS)',
      s4d: 'Dewelopowane w Unity 6. Aktualnie na laptopie z GPU RTX 4050 gra osiąga 130-140 FPS w zależności od mapy i jej optymalizacji.',
      s5t: 'Wiele Planów Rozwoju',
      s5d: 'Dokończenie gameplayu z punktacją i możliwością wygrania/przegrania (aktualnie sandbox do testowania rozwiązań), dopracowanie konfiguracji wyczucia kontrolerów (możliwość gry na myszce i na padach), dostosowanie do współczesnych standardów oraz optymalizacja w celu możliwości uruchamiania całego setupu (4 graczy + VR) na słabszych maszynach.',
      youtubeCta: 'Rozgrywka na YouTube',
      youtubeDesc: 'Playtesty, aktualizacje buildów i pełne mecze na splitscreenie.',
      youtubeChannelLink: 'Zobacz kanał YT z wszystkimi filmami',
      carouselBadge: 'Rozgrywka',
      carouselTitle: 'Zajawka Gameplayi',
    },
    avionics: {
      name: 'Skycodee.Avionics',
      tagline: 'Rój dronów dla ludzi — drony zrzutowe i oko na niebie.',
      pitch: 'Grupa budująca drony, pracująca nad rojem do codziennych zastosowań: małe drony zrzutowe przenoszące ładunek wielkości mniej więcej trzech ziemniaków, wyposażone w szczypce, oraz dron obserwator koordynujący rój z góry.',
      badge: 'Rój dronów',
      f1: 'Drony zrzutowe ze szczypcami',
      f2: 'Ładunek wielkości ok. trzech ziemniaków',
      f3: 'Dron obserwator jako oko roju',
      heroTitle: 'Rój dronów dla ludzi',
      heroLead: 'Nie program obronny i nie zabawka — practical swarm, który coś podnosi, przenosi i odkłada dokładnie tam, gdzie trzeba.',
      s1t: 'Dron zrzutowy',
      s1d: 'Przenosi ładunek wielkości mniej więcej trzech ziemniaków. Szczypce na serwie chwytają, trzymają i zwalniają paczkę bez lądowania.',
      s2t: 'Dron obserwator',
      s2d: 'Krąży nad rojem, utrzymuje mapę, śledzi każdą jednostkę i rozdziela cele. Reszta lata lekko, bo to on patrzy.',
      s3t: 'Koordynacja roju',
      s3d: 'Wspólna pozycja i stan zadań w całej flocie, żeby jednostki dzieliły robotę zamiast się o nią ścigać.',
      s4t: 'Budowane otwarcie',
      s4d: 'Gotowe ramy, własne mocowania, drukowane szczypce i nasza logika lotu — iterowane w terenie, nie na slajdzie.',
      shot1: 'Dron zrzutowy ze szczypcami',
      shot2: 'Dron obserwator — nadzór nad rojem',
      shot3: 'Formacja roju nad strefą zrzutu',
      specPayload: 'Ładunek',
      specPayloadV: '~3 ziemniaki',
      specGrip: 'Chwyt',
      specGripV: 'Szczypce na serwie',
      specRole: 'Obserwator',
      specRoleV: 'Oko na niebie',
    },
    teach: {
      name: 'Skycodee.TeachAndLearn',
      tagline: 'Gra AI, która uczy dzieci słówek — głosem mamy.',
      pitch: 'Przytulna, spersonalizowana gra edukacyjna. Generowane sceny umieszczają dziecko w jego ulubionym otoczeniu, głos z ElevenLabs ożywia opowieść, a własny głos rodzica prowadzi je słowo po słowo — w dowolnym języku.',
      badge: 'Gra edukacyjna AI',
      f1: 'Nauka słów w dowolnym języku',
      f2: 'Generowane, przytulne ulubione światy',
      f3: 'Prowadzenie głosem mamy',
      heroTitle: 'Nauka słów głosem, któremu ufają',
      heroLead: 'Dzieci uczą się języka od ludzi, których kochają. Zbudowaliśmy grę, która zostawia ten głos w centrum — a resztę bierze na siebie AI.',
      s1t: 'Głos mamy przy każdym słowie',
      s1d: 'Wystarczy krótka próbka głosu. Synteza ElevenLabs czyta potem każde polecenie, podpowiedź i pochwałę głosem rodzica.',
      s2t: 'Dowolny język',
      s2d: 'Wybierz język, którego dziecko się uczy, i ten, który już zna. Gra łączy je słowo po słowo.',
      s3t: 'Przytulne generowane światy',
      s3d: 'Generowanie obrazów buduje ulubione otoczenie dziecka — leśną kryjówkę, stację kosmiczną, kuchnię babci — i uczy słów, które w nim żyją.',
      s4t: 'Personalizowane tempo',
      s4d: 'Gra pamięta, co zostało w głowie, a co nie, i po cichu wraca do trudnych słów, aż wsiąkną.',
      shot1: 'Leśna kryjówka — zwierzęta i kolory',
      shot2: 'Karta słowa z poleceniem głosem mamy',
      shot3: 'Stacja kosmiczna — przedmioty i czasowniki',
      techVoice: 'Głos',
      techVoiceV: 'ElevenLabs',
      techImage: 'Sceny',
      techImageV: 'Generowanie obrazów',
      techLang: 'Języki',
      techLangV: 'Dowolna para',
    },
    cta: {
      title: 'Budujesz coś, co lata, gra albo uczy?',
      lead: 'Każdego roku podejmujemy się kilku projektów partnerskich we wszystkich czterech działach.',
      button: 'Napisz do Skycodee',
    },
    footer: {
      tagline: 'AI · GameDev · Awionika · Teach&Learn',
      divisions: 'Działy',
      note: 'To strona prezentacyjna. Domena produkcyjna: skycodee.com',
      rights: 'Wszelkie prawa zastrzeżone.',
    },
    contact: {
      name: 'Skycodee.Kontakt',
      badge: 'Siedziba & Kontakt',
      heroTitle: 'Siedziba Studia & Kontakt',
      heroLead: 'Napisz do Skycodee lub dowiedz się więcej o naszej autonomicznej strukturze inżynierskiej z siedzibą w Poznaniu.',
      aboutTitle: 'O nas',
      aboutText: 'Jesteśmy firmą wytwarzającą oprogramowanie. Ludzki inżynier nadzoruje tutaj jedynie maszyny 24h poprzez narzędzia alertująco-monitorujące i interfejs komunikacji z maszynami. W tym czasie rozwijane jest oprogramowanie z kilku branż.',
      originTitle: 'Nasze Korzenie',
      originText: 'Firma wyrosła z doświadczeń wielu lat programowania wyniesionego z dzielnicy Główna w Poznaniu.',
      authorTitle: 'Autor & Główny Inżynier',
      authorTagline: 'Inżynieria systemów, sztuczna inteligencja i architektura oprogramowania',
      mapTitle: 'Lokalizacja Siedziby Skycode',
      mapSubtitle: 'Główna, Poznań, Polska',
      pinLabel: 'Siedziba Skycode',
      directContact: 'Bezpośredni Kontakt',
      emailUs: 'Wyślij e-mail',
      sendMessage: 'Napisz do nas',
    },
  },
};

export function getNestedTranslation(source: Translations, key: string): string {
  const value = key.split('.').reduce<unknown>((obj, k) => {
    if (obj && typeof obj === 'object' && k in (obj as Record<string, unknown>)) {
      return (obj as Record<string, unknown>)[k];
    }
    return '';
  }, source);
  return typeof value === 'string' ? value : '';
}

export function interpolate(text: string, values: Record<string, string | number>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return values[key]?.toString() || match;
  });
}